import {
  KineticModule,
  PrivilegeType,
  useVideoCameraGoToPresetMutation,
  VideoStreamAccessType,
} from '@kinetic-ui/data-layer';
import clsx from 'clsx';
import { useCustomSnackbar } from 'components';
import CameraDetailsOverlay from 'components/CameraDetailsOverlay';
import PTZ from 'components/PTZ';
import SnapshotPreview from 'components/SnapshotPreview';
import VideoPlayer, { StreamType } from 'components/VideoPlayer';
import { URL_VALIDATION } from 'const';
import { useAllPrivileges } from 'kineticUtils';
import React, {
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { JoystickContext, VideoHotkeyAction } from 'contexts';
import { UserContext } from 'contexts/UserContext';
import { VideoPlayerFunctionality } from 'types/videoPlayerTypes';
import { CameraSelectionContext, PTZContext, usePtzContext } from './context';
import useExtendedVideoPlayerStyles from './styles';
import { ControlledVideoPlayerProps } from './types';

const ExtendedVideoPlayer: React.FunctionComponent<ControlledVideoPlayerProps> = (props) => {
  const classes = useExtendedVideoPlayerStyles();
  const {
    camera,
    tour,
    videoPlayerFunctionalities,
    removeFromLayout,
    goFullScreen,
    exitFullScreen,
    retry,
    setStreamLoaded,
    refetchData,
    isTourPaused,
    toggleIsTourPaused,
    addonsRight,
    backButtonConfig,
    showDetails = true,
    cameraDetailRoute,
  } = props;
  const { selectedCameraId } = useContext(CameraSelectionContext);
  const { id: userId } = useContext(UserContext);
  const hasPTZPrivilege = useAllPrivileges([PrivilegeType.Ptz], KineticModule.Video);
  const [hideOverlay, setHideOverlay] = useState<boolean>(false);
  const [showPTZControls, setShowPTZControls] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const { t } = useTranslation(['video']);
  const cameraId = useMemo(() => camera.id, [camera.id]);
  const ptzContext = usePtzContext(cameraId);
  const { currentAction } = useContext(JoystickContext);
  const [cameraGoToPreset] = useVideoCameraGoToPresetMutation();
  const videoContainer = useRef<HTMLDivElement>(null);

  const urlValidate = new RegExp(URL_VALIDATION);

  const handleSetStreamLoaded = useCallback(
    (streamLoaded: SetStateAction<boolean>) => {
      setStreamLoaded?.(streamLoaded);
      setLoaded(streamLoaded);
    },
    [setStreamLoaded]
  );

  useEffect(() => {
    if (
      selectedCameraId !== cameraId ||
      !currentAction ||
      currentAction.type !== VideoHotkeyAction.GO_TO_PRESET ||
      !camera?.presets?.length ||
      !currentAction?.payload
    ) {
      return;
    }
    try {
      const presetIndex = parseInt(currentAction.payload) - 1;
      const preset = camera.presets[presetIndex];
      if (preset) {
        cameraGoToPreset({
          variables: {
            cameraId: camera.id,
            presetId: preset.id,
          },
        });
      }
    } catch (error) {
      console.error('Error while trying to go to preset', error);
    }
  }, [camera.id, camera.presets, cameraGoToPreset, cameraId, currentAction, selectedCameraId]);

  const { enqueueCustomSnackbar } = useCustomSnackbar();

  const [isPTZDisabled, setPTZDisabled] = useState<boolean>(hasPTZPrivilege);

  const hasStreamManager = useMemo(() => {
    const privateStream = camera.streams?.find(
      (stream) => stream.accessType === VideoStreamAccessType.Private
    );
    if (!privateStream) {
      return false;
    }
    return !!privateStream.streamManager;
  }, [camera.streams]);

  const hasPTZControls = useMemo(
    () => videoPlayerFunctionalities.includes(VideoPlayerFunctionality.PTZ),
    [videoPlayerFunctionalities]
  );

  const stream = camera.streams?.[0];
  const url = stream && stream.url;
  const applicationName = stream?.application ?? '';
  const streamName = stream?.streamName ?? '';
  const streamType = (stream?.streamType ?? 'WEBRTC') as StreamType;
  const { ptzLock } = camera;

  const moveCamera = () => {
    if (!ptzLock.isLocked) {
      // require camera to be locked explicitly, deny any ptz movement
      if (ptzLock.requireExplicitCameraControlLocking) {
        enqueueCustomSnackbar?.warning(t('lockBeforeUsage'), {});
        setPTZDisabled(true);
        return;
      }

      setPTZDisabled(false);
      hasPTZControls && setHideOverlay(true);
      return;
    }
    const isLockedByCurrentUser = camera?.ptzLock?.userId === userId ? true : false;
    if (!isLockedByCurrentUser) {
      // camera is locked by another user so it can't be moved
      setPTZDisabled(true);
      enqueueCustomSnackbar?.warning(t('lockedBy', { fullName: ptzLock.fullName || '' }), {});
      return;
    }
    if (isLockedByCurrentUser) {
      // camera is locked by the current user so it can be moved
      setPTZDisabled(false);
      hasPTZControls && setHideOverlay(true);
      return;
    }
  };

  useEffect(() => {
    if (selectedCameraId === cameraId) {
      moveCamera();
      setHideOverlay(false);
    }
    // only run this effect when the selectedCameraId changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraId, selectedCameraId]);

  const containerSize = useMemo(() => {
    if (videoContainer.current) {
      return {
        width: videoContainer.current.clientWidth!,
        height: videoContainer.current.clientHeight!,
      };
    }
    return null;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoContainer.current?.clientWidth, videoContainer.current?.clientHeight]);

  return (
    <>
      {url && urlValidate.test(url) && (
        <div
          className={clsx(classes.playerWrapper, {
            [classes.noVisibility]: hasStreamManager && !loaded,
          })}
          ref={videoContainer}
        >
          <div
            className={classes.videoPlayer}
            onMouseDownCapture={() => hasPTZPrivilege && moveCamera()}
            onWheel={() => hasPTZPrivilege && moveCamera()}
            onKeyDown={() => hasPTZPrivilege && moveCamera()}
            onMouseUp={() => hasPTZControls && setHideOverlay(false)}
          >
            <PTZContext.Provider value={ptzContext}>
              <VideoPlayer
                url={url}
                applicationName={applicationName}
                streamName={streamName}
                streamType={streamType}
                setShowPTZControls={setShowPTZControls}
                retry={retry}
                setStreamLoaded={handleSetStreamLoaded}
                settings={camera?.communicationParameters}
              />
              {!isPTZDisabled && hasPTZControls && showPTZControls && hasPTZPrivilege && (
                <PTZ cameraId={camera.id} />
              )}
            </PTZContext.Provider>
          </div>
          {showDetails && (
            <CameraDetailsOverlay
              videoPlayerFunctionalities={videoPlayerFunctionalities}
              data={camera}
              tour={tour}
              removeFromLayout={removeFromLayout}
              goFullScreen={goFullScreen}
              exitFullScreen={exitFullScreen}
              hideWhenDragged={hideOverlay}
              refetchData={refetchData}
              isTourPaused={isTourPaused}
              toggleIsTourPaused={toggleIsTourPaused}
              addonsRight={addonsRight}
              backButtonConfig={backButtonConfig}
              cameraDetailRoute={cameraDetailRoute}
              containerSize={containerSize}
            />
          )}
        </div>
      )}
      {hasStreamManager && !loaded && <SnapshotPreview cameraId={camera.id} />}
    </>
  );
};

export default ExtendedVideoPlayer;
